import { CompanyRole } from "@/api/companies";
import { TApiResponse } from "@/types/api";
import { handleAsync } from "@/utils";

import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TCompanyReadinessReviewData,
  TFundraiserSummaryReview,
  TFundraiserSummaryReviewData,
  TInvestorReadinessReview,
  TInvestorSummaryReview,
  TInvestorSummaryReviewData,
  TReviewFormValues,
  TReviewerCompany,
  TReviewerDocumentList,
} from "./reviewer.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class ReviewerService {
  private rootEndpoint = "reviewer" as const;

  searchFundraiserCompanies(query: string, options?: TApiServiceOptions) {
    const normalizedQuery = query.trim().toLowerCase();
    return handleAsync<TApiServiceError, TApiResponse<TReviewerCompany[]>>(async () => {
      const response = await apiService.get<TApiResponse<TReviewerCompany[]>>("admin/companies/?limit=100", options);
      const companies = response.data.filter((company) => {
        const isFundraiser = company.role === CompanyRole.FUNDRAISER;
        if (!normalizedQuery) return isFundraiser;
        return isFundraiser && company.name.toLowerCase().includes(normalizedQuery);
      });

      return {
        ...response,
        data: companies,
      };
    });
  }

  getCompanyReadiness(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyReadinessReviewData[]>>(() =>
      apiService.get<TApiResponse<TCompanyReadinessReviewData[]>>(
        `${this.rootEndpoint}/data/company-readiness?company_id=${companyId}&limit=10`,
        options
      )
    );
  }

  getFundraiserSummaries(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFundraiserSummaryReviewData[]>>(() =>
      apiService.get<TApiResponse<TFundraiserSummaryReviewData[]>>(
        `${this.rootEndpoint}/data/fundraiser-summaries?fundraiser_company_id=${companyId}`,
        options
      )
    );
  }

  getInvestorSummaries(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorSummaryReviewData[]>>(() =>
      apiService.get<TApiResponse<TInvestorSummaryReviewData[]>>(
        `${this.rootEndpoint}/data/investor-summaries?fundraiser_company_id=${companyId}`,
        options
      )
    );
  }

  getFundraiserDocuments(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TReviewerDocumentList>>(() =>
      apiService.get<TApiResponse<TReviewerDocumentList>>(
        `${this.rootEndpoint}/fundraiser-documents?company_id=${companyId}`,
        options
      )
    );
  }

  downloadFundraiserDocument(fileId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, { blob: Blob; filename: string | null }>(async () => {
      const response = await apiService.getRaw(`${this.rootEndpoint}/fundraiser-documents/${fileId}/download`, options);
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") ?? response.headers.get("Content-Disposition");
      const filenameMatch = disposition?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      let filename = filenameMatch?.[1] ?? null;

      if (filename) {
        try {
          filename = decodeURIComponent(filename);
        } catch {
          // Use the raw filename when it is not URI-encoded.
        }
      }

      return { blob, filename };
    });
  }

  getInvestorReadinessReviews(analysisId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorReadinessReview[]>>(() =>
      apiService.get<TApiResponse<TInvestorReadinessReview[]>>(
        `${this.rootEndpoint}/reviews/investor-readiness?company_readiness_analysis_id=${analysisId}`,
        options
      )
    );
  }

  getFundraiserSummaryReviews(summaryId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TFundraiserSummaryReview[]>>(() =>
      apiService.get<TApiResponse<TFundraiserSummaryReview[]>>(
        `${this.rootEndpoint}/reviews/fundraiser-summaries?fundraiser_summary_for_investors_id=${summaryId}`,
        options
      )
    );
  }

  getInvestorSummaryReviews(summaryId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorSummaryReview[]>>(() =>
      apiService.get<TApiResponse<TInvestorSummaryReview[]>>(
        `${this.rootEndpoint}/reviews/investor-summaries?investor_summary_for_fundraisers_id=${summaryId}`,
        options
      )
    );
  }

  saveInvestorReadinessReview(
    analysisId: string,
    reviewId: string | null,
    values: TReviewFormValues,
    options?: TApiServiceOptions
  ) {
    const payload = {
      company_readiness_analysis_id: analysisId,
      positive_critique_of_ai: values.positive_critique_of_ai ?? null,
      negative_critique_of_ai: values.negative_critique_of_ai ?? null,
      reviewer_summary: values.reviewer_summary ?? null,
    };

    return handleAsync<TApiServiceError, TApiResponse<TInvestorReadinessReview>>(() =>
      reviewId
        ? apiService.patch<TApiResponse<TInvestorReadinessReview>>(
            `${this.rootEndpoint}/reviews/investor-readiness/${reviewId}`,
            payload,
            options
          )
        : apiService.post<TApiResponse<TInvestorReadinessReview>>(
            `${this.rootEndpoint}/reviews/investor-readiness`,
            payload,
            options
          )
    );
  }

  saveFundraiserSummaryReview(
    summaryId: string,
    reviewId: string | null,
    values: TReviewFormValues,
    options?: TApiServiceOptions
  ) {
    const payload = {
      fundraiser_summary_for_investors_id: summaryId,
      positive_critique_of_ai: values.positive_critique_of_ai ?? null,
      negative_critique_of_ai: values.negative_critique_of_ai ?? null,
      reviewers_summary: values.reviewer_summary ?? null,
    };

    return handleAsync<TApiServiceError, TApiResponse<TFundraiserSummaryReview>>(() =>
      reviewId
        ? apiService.patch<TApiResponse<TFundraiserSummaryReview>>(
            `${this.rootEndpoint}/reviews/fundraiser-summaries/${reviewId}`,
            payload,
            options
          )
        : apiService.post<TApiResponse<TFundraiserSummaryReview>>(
            `${this.rootEndpoint}/reviews/fundraiser-summaries`,
            payload,
            options
          )
    );
  }

  saveInvestorSummaryReview(
    summaryId: string,
    reviewId: string | null,
    values: TReviewFormValues,
    options?: TApiServiceOptions
  ) {
    const payload = {
      investor_summary_for_fundraisers_id: summaryId,
      positive_critique_of_ai: values.positive_critique_of_ai ?? null,
      negative_critique_of_ai: values.negative_critique_of_ai ?? null,
      reviewers_summary: values.reviewer_summary ?? null,
    };

    return handleAsync<TApiServiceError, TApiResponse<TInvestorSummaryReview>>(() =>
      reviewId
        ? apiService.patch<TApiResponse<TInvestorSummaryReview>>(
            `${this.rootEndpoint}/reviews/investor-summaries/${reviewId}`,
            payload,
            options
          )
        : apiService.post<TApiResponse<TInvestorSummaryReview>>(
            `${this.rootEndpoint}/reviews/investor-summaries`,
            payload,
            options
          )
    );
  }
}

export const reviewerService = new ReviewerService();
